import {
  type ReorderConflictResponseFragment,
  type ReorderLineItemMutation,
  type ReorderOrderMutation,
  type ReorderSuccessResponseFragment,
} from '../GraphQL/Reorder.generated';
import { validReorderEventKeys } from '../machines/Reorder.machine';

import type { ReorderEvent } from '../machines/Reorder.machine';
import type { StateValue } from 'xstate5';

export { reorderLogger as logger } from '../Reorder.logger';

// Extracts reorder values from event.
export const getValidEventKeys = (event?: Partial<ReorderEvent>): Partial<ReorderEvent> => {
  return validReorderEventKeys
    .map((key) => ({ [key]: event?.[key] }))
    .filter(Boolean)
    .reduce((acc, partial) => ({ ...acc, ...partial }), {});
};

export const justStartedReorder = (reorderStep?: StateValue) => {
  return (reorderStep as string) === 'idle';
};

export const cameFromLocationConfirmation = (reorderStep?: StateValue) => {
  return reorderStep === 'locationConfirmation';
};

export const cameFromLocationSearch = (reorderStep?: StateValue) => {
  return reorderStep === 'locationSearch';
};

export const handleConflictTelemetry = ({
  isCannotReorder,
  isMaxDelivery,
  isDressingUnavailable,
  isMissingMenu,
  cannotReorderTelemetry,
  maxDeliveryTelemetry,
  dressingUnavailableTelemetry,
  missingMenuTelemetry,
}: ConflictTelemetry) => {
  if (isCannotReorder) cannotReorderTelemetry();
  else if (isMaxDelivery) maxDeliveryTelemetry();
  else if (isMissingMenu) missingMenuTelemetry();
  else if (isDressingUnavailable) dressingUnavailableTelemetry();
};

// Extracts reorder result and conflict type from either reorder order or reorder line item.
export const parseConflictResults = (data: unknown) => {
  const reorderOrderResult = (data as ReorderOrderMutation)?.reorderOrder;
  const reorderItemResult = (data as ReorderLineItemMutation)?.reorderLineItem;
  const result = reorderOrderResult ?? reorderItemResult;
  const conflicts = (result as ReorderConflictResponseFragment)?.conflicts ?? [];
  const isMixingSupported = (result as ReorderSuccessResponseFragment)?.isMixingSupported;
  const reorderResult = result?.__typename;
  const conflictType = conflicts[0]?.type;

  return {
    conflictType,
    reorderResult,
    isDressingUnavailable: !isMixingSupported,
    isCannotReorder: reorderResult === 'CanNotReorder',
    isMaxDelivery: reorderResult === 'RestaurantMaxDeliveryQuantityExceeded',
    isMissingMenu: conflictType === 'PRODUCT_UNAVAILABLE',
    isUnauthorizedModification: reorderResult === 'UnauthorizedOrderModification',
  };
};

type ConflictTelemetry = Readonly<{
  isCannotReorder: boolean;
  isMaxDelivery: boolean;
  isDressingUnavailable: boolean;
  isMissingMenu: boolean;
  cannotReorderTelemetry: () => void;
  maxDeliveryTelemetry: () => void;
  dressingUnavailableTelemetry: () => void;
  missingMenuTelemetry: () => void;
}>;
